import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { TextField, validateCvr, OverlayDialog } from '@flos/react-components';
import { useGlobalDispatch, useGlobalState } from '../../common/context/AppContext';
import { baseStyles } from '../../theme/base.styles';
import { IAppProps, REDUCER_KEY } from '../../common/interface/IAppProvider';
import { EMAIL_EVENT } from '../../common/interface/IPayload';
import { ROUTE_NAMES } from '../../router/RouterApp';
import GridWrapper from '../../components/shared/grid-wrapper/GridWrapper';
import RenderHtml from '../../components/shared/render-html/RenderHtml';
import { getCvrData } from '../../common/async/AsyncCalls';
import { track, errorTypes } from 'c9-tracking';
import { getBusinessLine } from '../package/PriceCalculator';
import PartnerInfo from '../../components/shared/partner-info/PartnerInfo';

interface IModalProps {
    modalStatus: boolean;
    modalContent: JSX.Element;
}

const errorText: any = {
    CVR_NOT_FOUND: 'Vi fandt desværre ingen oplysninger om dette CVR-nummer',
    INVALID_REQUEST: 'Ugyldigt CVR-nummer eller fejl i forespørgsel',
    CVR_INVALID: 'Et CVR-nummer skal indeholde 8 tal',
    UNKNOWN_ERROR: 'Ukendt fejl. Prøv venligst igen senere',
};

const CvrScreenView: FunctionComponent<IAppProps> = (props): JSX.Element => {
    const hippoTexts = props.config.resourceBundles['onsa-erhvervsforsikring/'].read();
    const cb_uc = props.config.contentBlocks['unknown-cvr'].read();
    const cb_icv = props.config.contentBlocks['indtast-cvr-nummer-v2'].read();
    const baseClasses = baseStyles();
    const { initialState, cvrData } = useGlobalState();
    const appDispatch = useGlobalDispatch();
    const [submitted, setSubmitted] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [errorType, setErrorType] = useState('');
    const navigate = useNavigate();

    const [modal, setModal] = useState<IModalProps>({
        modalStatus: false,
        modalContent: (
            <React.Fragment>
                <Typography variant="subtitle1" color="textPrimary" className={baseClasses.cvrModal}>
                    <RenderHtml>{cb_uc.data}</RenderHtml>
                </Typography>
            </React.Fragment>
        ),
    });

    useEffect(() => {
        if (initialState) {
            appDispatch({ type: REDUCER_KEY.SET_INITIAL_STATE, payload: false });
        }

        if (errorType === 'CVR_NOT_FOUND') {
            toggleModal(true);
        }
    }, [appDispatch, initialState, errorType]);

    const nameFieldEventHandler = (event: any) => {
        setSubmitted(false);
        setErrorType('');
        appDispatch({ type: REDUCER_KEY.CVR_NUMBER, payload: event.value.trim() });
    };

    const submitEventHandler = async () => {
        if (validateCvr(cvrData.cvrNumber)) {
            setFetching(true);
            setSubmitted(false);
            setErrorType('');

            // GET CVR DATA
            getCvrData(props, appDispatch, cvrData.cvrNumber)
                .then(function (response) {
                    if (!response.ok) {
                        if (response.status === 400) {
                            track.fault('Invalid request for cvr/company information', errorTypes.tech);
                            throw Error('INVALID_REQUEST');
                        } else if (response.status === 404) {
                            track.fault('cvr-number could not be found', errorTypes.user);
                            throw Error('CVR_NOT_FOUND');
                        } else {
                            track.fault('Unknown error', errorTypes.tech);
                            throw Error('UNKNOWN_ERROR');
                        }
                    }
                    return response;
                })
                .then(function (response) {
                    response.json().then((jsonResponse) => {
                        const employeesStr = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 2 }).format(
                            jsonResponse.employees || 0,
                        );
                        const businessLine = getBusinessLine(jsonResponse.business.key);
                        appDispatch({
                            type: REDUCER_KEY.CVR_DATA,
                            payload: {
                                ...jsonResponse,
                                cvrNumber: `${jsonResponse.cvrNumber}`,
                                employees: Math.ceil(!jsonResponse.employees ? 0 : jsonResponse.employees),
                                employeesStr: employeesStr,
                                businessLine: businessLine,
                            },
                        });
                        appDispatch({ type: REDUCER_KEY.SET_STATE_CHANGE, payload: true });
                        track.form('cvr-form', [
                            { id: 'cvr-nummer', value: jsonResponse.cvrNumber },
                            { id: 'employees', value: employeesStr },
                            { id: 'businessLine', value: businessLine },
                        ]);
                    });

                    navigate(ROUTE_NAMES.COMPANY_INFO);
                })
                .catch(function (error) {
                    setErrorType(error.message);
                    setFetching(false);
                    track.form('cvr-form', [{ id: 'cvr-nummer', value: cvrData.cvrNumber }]);
                    track.fault('Fetching CVR data failed', errorTypes.tech);
                    appDispatch({ type: REDUCER_KEY.SET_EMAIL_EVENT, payload: EMAIL_EVENT.GENERAL_ERROR });
                    appDispatch({ type: REDUCER_KEY.LOADING_DATA, payload: false });
                    if (error.message !== 'CVR_NOT_FOUND') {
                        appDispatch({
                            type: REDUCER_KEY.SET_ERROR,
                            payload: { stepOne: true, stepTwo: false, separateCallMe: false },
                        });
                    }
                });
        } else {
            track.fault('Form submitted without cvr-number', errorTypes.user);
            setSubmitted(true);
        }
    };

    const handleFormatter = (k: string) => {
        if (/\d+/g.test(k) && parseInt(k) !== 0) {
            return k.slice(0, 8);
        }
        return '';
    };

    const toggleModal = (modalStatus: boolean) => {
        modalStatus
            ? track.navigate('CVR fault overlay open. Look in last track.fault for details')
            : track.navigate('CVR fault overlay closed');
        setModal((prev: IModalProps) => {
            return { ...prev, modalStatus };
        });
    };

    return (
        <GridWrapper header={hippoTexts['cvr-page-heading']}>
            <OverlayDialog
                disableScrollLock={true}
                open={modal.modalStatus}
                onClose={toggleModal.bind(null, false)}
                transitionDuration={0}
            >
                {modal.modalContent}
            </OverlayDialog>
            <RenderHtml>{cb_icv.data}</RenderHtml>
            <PartnerInfo {...props} />
            <Typography variant="subtitle1" color="textPrimary" style={{ marginBottom: '1rem' }}>
                {hippoTexts['cvr-company-question']}
            </Typography>
            <TextField
                id="onsa-cvr-number"
                required={true}
                data-testid="inputfield"
                label="CVR-nummer"
                fieldEvent={nameFieldEventHandler}
                value={cvrData.cvrNumber}
                disabled={fetching}
                format={handleFormatter}
                errorTextInvalid={errorText['CVR_INVALID']}
                validator={() => validateCvr(cvrData.cvrNumber)}
                error={(!validateCvr(cvrData.cvrNumber) && submitted) || errorType !== ''}
                isSubmitted={submitted}
                autoFocus
            />
            <Button onClick={submitEventHandler} fullWidth variant="contained" disabled={fetching}>
                {fetching ? (
                    <CircularProgress role="spinner" size={24} className={baseClasses.buttonProgress} />
                ) : (
                    hippoTexts['continue-btn-label']
                )}
            </Button>
        </GridWrapper>
    );
};

export default CvrScreenView;
